import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, clearSession, endpoints, useAuthSession } from '@/api';
import { useTranslations } from '@/i18n/TranslationsProvider';
import { cn } from '@/lib/cn';
import { disconnectPlayerSocket } from '@/player';
import { useTheme } from '@/theme';
import { Icon, buttonVariants } from '@/ui';
import { flagUrlForLanguage } from './flag-assets';
import { MenuBalanceCard } from './MenuBalanceCard';
import { MenuProfileNav } from './MenuProfileNav';

function chunkLanguages<T>(items: T[], size: number): T[][] {
  const rows: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    rows.push(items.slice(i, i + size));
  }
  return rows;
}

interface MenuLanguageDropdownProps {
  className?: string;
  loggedIn: boolean;
  onClose?: () => void;
}

export function MenuLanguageDropdown({ className, loggedIn, onClose }: MenuLanguageDropdownProps) {
  const { t, i18n } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const { user } = useAuthSession();
  const { languages, selectedLanguage, setSelectedLanguage, isLoadingTranslations } = useTranslations();
  const [loggingOut, setLoggingOut] = useState(false);
  const isDark = theme === 'dark';
  const hasLanguageOptions = languages.length > 0;
  const languageItems = hasLanguageOptions ? languages.slice(0, 15) : [{ Short: selectedLanguage }];
  const rows = chunkLanguages(languageItems, 5);
  const isTranslationReady =
    selectedLanguage === 'en' || i18n.hasResourceBundle(selectedLanguage, 'translation');
  const displayName = user?.Username ?? user?.Name ?? '';

  const label = (key: string, fallback: string) =>
    isTranslationReady ? t(key, { defaultValue: fallback }) : fallback;

  const onLanguageSelect = (nextLanguage: string) => {
    setSelectedLanguage(nextLanguage);
    onClose?.();
  };

  const onLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      const { host, path } = endpoints.player.logout;
      await api.post(host, path);
    } catch {
      // Always clear the local session even if the server call fails.
    } finally {
      disconnectPlayerSocket();
      clearSession();
      setLoggingOut(false);
      onClose?.();
    }
  };

  return (
    <div
      className={cn(
        'bg-chrome-header flex max-h-[calc(100dvh-9rem)] w-[252px] flex-col gap-3 overflow-y-auto overscroll-contain rounded-lg border border-border-default p-3 shadow-2xl lg:max-h-[calc(100dvh-4.5rem)] [scrollbar-gutter:stable]',
        className,
      )}
    >
      {loggedIn && (
        <p className="text-label-m font-bold text-text-primary">
          {label('Hello', 'Hello')}
          {displayName ? `, ${displayName}` : ''}
        </p>
      )}

      <div className="flex h-10 items-center gap-2">
        {loggedIn ? (
          <>
            <Link
              to="/profile/deposits"
              onClick={onClose}
              className={cn(buttonVariants({ variant: 'primary', size: 'sm' }), 'flex-1')}
            >
              {label('Deposit', 'Deposit')}
            </Link>
            <Link
              to="/profile/details"
              onClick={onClose}
              className={cn(buttonVariants({ variant: 'secondary', size: 'sm' }), 'flex-1')}
            >
              {label('My Profile', 'My Profile')}
            </Link>
          </>
        ) : (
          <>
            <Link
              to="/login"
              search={{ redirect: undefined }}
              onClick={onClose}
              className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }), 'flex-1')}
            >
              {label('Login', 'Login')}
            </Link>
            <Link
              to="/register"
              onClick={onClose}
              className={cn(buttonVariants({ variant: 'primary', size: 'sm' }), 'flex-1')}
            >
              {label('Register', 'Register')}
            </Link>
          </>
        )}
      </div>

      {loggedIn && <MenuBalanceCard className="shrink-0" />}
      {loggedIn && <MenuProfileNav onClose={onClose} />}

      <div className="flex h-7 items-center justify-between">
        <span className="text-label-s font-bold text-text-primary">{label('Theme', 'Theme')}</span>
        <div className="flex items-center gap-2">
          <Icon name="darkmode" size={16} className="text-icon-muted" />
          <button
            type="button"
            onClick={toggleTheme}
            aria-label={`Switch to ${isDark ? 'light' : 'dark'} theme`}
            className="relative flex h-7 w-12 items-center rounded-full bg-action-primary px-1"
          >
            <span
              className={cn(
                'size-[18px] rounded-full bg-white transition-transform',
                isDark ? 'translate-x-0' : 'translate-x-6',
              )}
            />
          </button>
          <Icon name="lightmode" size={16} className="text-icon-muted" />
        </div>
      </div>

      <div>
        <span className="text-caption text-text-secondary">{label('Languages', 'Languages')}</span>
        <div className="mt-2 flex flex-col gap-2">
          {rows.map((row, rowIndex) => (
            <div key={rowIndex} className="flex items-center justify-between gap-2">
              {row.map((language) => {
                const code = language.Short;
                const flagUrl = flagUrlForLanguage(code);
                const isSelected = selectedLanguage.toLowerCase() === code.toLowerCase();
                return (
                  <button
                    key={code}
                    type="button"
                    disabled={isLoadingTranslations}
                    onClick={() => onLanguageSelect(code)}
                    aria-label={language.Name ?? code.toUpperCase()}
                    className={cn(
                      'relative size-7 overflow-hidden rounded-full border bg-bg-surface',
                      isSelected ? 'border-2 border-action-primary' : 'border-border-default',
                      isLoadingTranslations && 'cursor-not-allowed opacity-70',
                    )}
                  >
                    {flagUrl ? (
                      <img
                        src={flagUrl}
                        alt=""
                        className="absolute top-1/2 left-1/2 h-7 w-10 -translate-x-1/2 -translate-y-1/2 object-cover"
                      />
                    ) : (
                      <span className="text-[10px] text-text-primary">{code.toUpperCase()}</span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {loggedIn && (
        <button
          type="button"
          onClick={() => void onLogout()}
          disabled={loggingOut}
          className={cn(
            buttonVariants({ variant: 'ghost', size: 'sm' }),
            'w-full gap-1 disabled:opacity-70',
          )}
        >
          <Icon name="logout" size={16} className="text-icon-default" />
          {label('Logout', 'Logout')}
        </button>
      )}
    </div>
  );
}
